import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router";
import Joyride, { STATUS } from "react-joyride";
import {
  Brain, Search, X, ChevronRight, Sparkles, Compass, BookOpen,
  Cpu, HeartPulse, Palette, BarChart2, BookMarked, Scale, GraduationCap, Leaf, Telescope,
  Loader2, CheckCircle2, Plus,
} from "lucide-react";
import { ExplorerHeader } from "../components/ui/career-explorer/explorer-header";
import { CareerIllustration } from "../components/ui/career-explorer/CareerIllustration";
import { Sidebar } from "../components/ui/sidebar";
import { UniversalSearch } from "../components/ui/universal-search";
import ProtectedRoute from "../components/ProtectedRoute";
import { StorageService } from "@/hooks/services/storageService";
import { AuthStore } from "@/hooks/stores/AuthStore";
import { StudentStore } from "@/hooks/stores/StudentStore";
import { getAllNodes } from "@/data/career-tree";
import { CareerPathCard } from "@/components/ui/carreer-path/career-path-card";

// ─── Broad path metadata (mirrors CareerPath.jsx) ────────────────────────────
const BROAD_PATH_META = {
  broad_stem_tech:            { category: "Technology", categoryColor: "text-blue-500",   title: "STEM & Technology",          description: "Engineering, computing, mathematics and pure sciences." },
  broad_health_medical:       { category: "Health",     categoryColor: "text-emerald-500", title: "Health & Medical Sciences",  description: "Medicine, nursing, pharmacy and public health." },
  broad_creative_arts:        { category: "Creative",   categoryColor: "text-pink-500",    title: "Creative Arts & Design",     description: "Fine arts, music, fashion and media." },
  broad_business_finance:     { category: "Business",   categoryColor: "text-amber-500",   title: "Business & Finance",         description: "Accounting, banking, marketing and entrepreneurship." },
  broad_humanities_social:    { category: "Humanities", categoryColor: "text-violet-500",  title: "Humanities & Social Sciences",description: "Communication, psychology, sociology and history." },
  broad_law_governance:       { category: "Law",        categoryColor: "text-slate-500",   title: "Law & Governance",           description: "Law, politics, public administration and diplomacy." },
  broad_education_training:   { category: "Education",  categoryColor: "text-cyan-500",    title: "Education & Training",       description: "Teaching and training at every level." },
  broad_agriculture_environment:{ category: "Agriculture",categoryColor: "text-lime-500",  title: "Agriculture & Environment",  description: "Farming, food science, fisheries and environmental management." },
};

// ─── Visual config by broad category (used for category badges/chips) ────────

const CATEGORY_STYLE = {
  STEM:        { bg: "bg-blue-50",     border: "border-blue-200",   text: "text-blue-700",   dot: "bg-blue-500",    Icon: Cpu },
  Health:      { bg: "bg-emerald-50",  border: "border-emerald-200",text: "text-emerald-700",dot: "bg-emerald-500", Icon: HeartPulse },
  Creative:    { bg: "bg-rose-50",     border: "border-rose-200",   text: "text-rose-700",   dot: "bg-rose-500",    Icon: Palette },
  Business:    { bg: "bg-amber-50",    border: "border-amber-200",  text: "text-amber-700",  dot: "bg-amber-500",   Icon: BarChart2 },
  Humanities:  { bg: "bg-violet-50",   border: "border-violet-200", text: "text-violet-700", dot: "bg-violet-500",  Icon: BookMarked },
  Law:         { bg: "bg-slate-100",   border: "border-slate-300",  text: "text-slate-700",  dot: "bg-slate-500",   Icon: Scale },
  Education:   { bg: "bg-cyan-50",     border: "border-cyan-200",   text: "text-cyan-700",   dot: "bg-cyan-500",    Icon: GraduationCap },
  Agriculture: { bg: "bg-lime-50",     border: "border-lime-200",   text: "text-lime-700",   dot: "bg-lime-500",    Icon: Leaf },
};

const DEFAULT_STYLE = { bg: "bg-slate-100", border: "border-slate-200", text: "text-slate-600", dot: "bg-slate-400", Icon: Telescope };

function getCategoryStyle(cat) {
  return CATEGORY_STYLE[cat] ?? DEFAULT_STYLE;
}

// ─── Match badge ───────────────────────────────────────────────────────────────

function MatchBadge({ pct }) {
  if (pct == null) return null;
  const cls = pct >= 75 ? "bg-emerald-100 text-emerald-700 border-emerald-200"
            : pct >= 55 ? "bg-blue-100 text-blue-700 border-blue-200"
            : pct >= 40 ? "bg-amber-100 text-amber-700 border-amber-200"
            : "bg-slate-100 text-slate-500 border-slate-200";
  return (
    <span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${cls}`}>
      {pct}% match
    </span>
  );
}

// ─── Score a field node against a trait profile ────────────────────────────────
// Uses minimumThreshold as the weight — mirrors broadPathMatcher logic.

function scoreNode(node, profile) {
  if (!profile || Object.keys(profile).length === 0) return null;
  let rawScore = 0;
  let maxScore = 0;
  for (const { traitId, minimumThreshold } of node.traits) {
    const studentScore = profile[traitId] ?? 0;
    rawScore += (studentScore / 100) * minimumThreshold;
    maxScore += minimumThreshold;
  }
  return maxScore > 0 ? Math.round((rawScore / maxScore) * 100) : null;
}

// Top 2 traits where student's score is closest to (or exceeds) the threshold
function getTopMatchingTraits(node, profile) {
  if (!profile || !node.traits?.length) return [];
  return [...node.traits]
    .map((tr) => ({
      name: tr.name,
      score: profile[tr.traitId] ?? 0,
      threshold: tr.minimumThreshold,
      ratio: (profile[tr.traitId] ?? 0) / tr.minimumThreshold,
    }))
    .sort((a, b) => b.ratio - a.ratio)
    .slice(0, 2);
}

// ─── Career card — mirrors CareerPathCard theming ─────────────────────────────

function CareerCard({ node, matchPct, traitProfile, onViewPath, onAddToPath, isAdded }) {
  const style = getCategoryStyle(node.broadCategory);

  return (
    <div className="flex flex-col rounded-xl bg-slate-100 shadow-md transition-shadow hover:shadow-lg">
      {/* Illustration header */}
      <div className="relative rounded-t-xl overflow-hidden h-24 object-cover">
        <CareerIllustration
          nodeId={node.nodeId}
          broadCategory={node.broadCategory}
          className="w-full h-full"
        />
        {/* Match badge — top right */}
        {matchPct != null && (
          <div className="absolute top-2.5 right-2.5">
            <MatchBadge pct={matchPct} />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex flex-1 flex-col p-4">
        {/* Category */}
        <p className={`text-sm font-medium ${style.text}`}>{node.broadCategory}</p>

        {/* Title */}
        <p className="text-lg font-bold text-slate-900">{node.name}</p>

        {/* Description */}
        <p className="mt-1 flex-1 text-sm text-slate-500 line-clamp-2">{node.description}</p>

        {/* Key traits */}
        {node.traits?.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {node.traits.slice(0, 3).map((tr) => (
              <span key={tr.traitId} className="text-[11px] bg-slate-200 text-slate-600 px-2 py-0.5 rounded-md font-medium">
                {tr.name}
              </span>
            ))}
          </div>
        )}

        {/* Why this career — personalized explanation */}
        <WhyThisCareer node={node} profile={traitProfile} />

        {/* Match progress bar — mirrors CareerPathCard progress */}
        <div className="mt-4 flex flex-col gap-2">
          <div className="flex justify-between text-xs font-medium text-slate-500">
            <span>Trait Match</span>
            <span>{matchPct != null ? `${matchPct}%` : "—"}</span>
          </div>
          <div className="h-2 w-full rounded-full bg-slate-200">
            <div
              className="h-2 rounded-full bg-yellow-400 transition-all duration-300"
              style={{ width: `${matchPct ?? 0}%` }}
            />
          </div>
        </div>

        {/* Buttons — same layout as CareerPathCard */}
        <div className="mt-4 space-y-2">
          <button
            onClick={() => onViewPath(node.nodeId)}
            className="w-full bg-blue-600/10 hover:bg-blue-600 text-blue-800 hover:text-white text-sm font-bold rounded-lg h-10 transition-all duration-200 flex items-center justify-center gap-1.5"
          >
            Explore Path <ChevronRight className="w-4 h-4" />
          </button>
          <button
            onClick={() => onAddToPath(node.nodeId)}
            disabled={isAdded}
            className={`w-full text-sm font-medium rounded-lg h-10 transition-all duration-200 flex items-center justify-center gap-2 ${
              isAdded
                ? "bg-emerald-100 text-emerald-600 border border-emerald-200 cursor-default"
                : "bg-emerald-50 hover:bg-emerald-500 text-emerald-600 hover:text-white border border-emerald-200 hover:border-emerald-500"
            }`}
          >
            {isAdded
              ? <><CheckCircle2 className="w-4 h-4" />In My Paths</>
              : <><Plus className="w-4 h-4" />Add to My Paths</>
            }
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── "Why this career?" personalized explanation ──────────────────────────────

function buildWhyExplanation(node, profile) {
  if (!profile || !node.traits?.length) return null;

  const ranked = [...node.traits]
    .map((tr) => ({
      name: tr.name,
      studentScore: profile[tr.traitId] ?? 0,
      threshold: tr.minimumThreshold,
      ratio: (profile[tr.traitId] ?? 0) / tr.minimumThreshold,
    }))
    .sort((a, b) => b.ratio - a.ratio);

  const strengths = ranked.filter((t) => t.ratio >= 0.85).slice(0, 2);
  const gaps = ranked.filter((t) => t.ratio < 0.6).slice(-2);

  if (strengths.length === 0) return null;

  const strengthText = strengths.map((t) => t.name).join(" and ");
  const gapText = gaps.length > 0 ? ` Growth area: ${gaps.map((t) => t.name).join(", ")}.` : "";

  return `Your ${strengthText} score${strengths.length > 1 ? "s align" : " aligns"} strongly with what this career demands.${gapText}`;
}

function WhyThisCareer({ node, profile }) {
  const [open, setOpen] = useState(false);
  const text = buildWhyExplanation(node, profile);
  if (!text) return null;

  return (
    <div className="mt-3">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 text-[11px] font-semibold text-indigo-600 hover:text-indigo-800 transition-colors"
      >
        <Sparkles className="w-3 h-3" />
        Why this career?
        <ChevronRight className={`w-3 h-3 transition-transform ${open ? "rotate-90" : ""}`} />
      </button>
      {open && (
        <p className="mt-1.5 text-xs text-slate-500 leading-relaxed bg-indigo-50 border border-indigo-100 rounded-lg px-3 py-2">
          {text}
        </p>
      )}
    </div>
  );
}

// ─── No-profile CTA ───────────────────────────────────────────────────────────

function NoProfileCTA({ navigate }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center max-w-sm mx-auto">
      <div className="w-16 h-16 rounded-2xl bg-indigo-100 border border-indigo-200 flex items-center justify-center mb-5">
        <Brain className="w-8 h-8 text-indigo-600" />
      </div>
      <h3 className="text-lg font-bold text-slate-900 mb-2">Take the Psychometric Assessment</h3>
      <p className="text-slate-500 text-sm mb-6">
        Complete the 32-question assessment to unlock personalized career suggestions ranked by how well they match your unique trait profile.
      </p>
      <button
        onClick={() => navigate("/psychometric-assessment")}
        className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold px-6 py-3 rounded-xl transition-colors"
      >
        Start Assessment <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
}

// ─── Joyride tooltip ──────────────────────────────────────────────────────────

const Animated3DTooltip = ({ index, step, backProps, primaryProps, skipProps, tooltipProps, isLastStep }) => (
  <div
    {...tooltipProps}
    className="tour-3d-box"
    style={{
      backgroundColor: "#ffffff", padding: "28px", borderRadius: "40px",
      border: "1px solid rgba(255,255,255,0.5)",
      boxShadow: "0 20px 40px rgba(22,154,144,0.15), 0 0 0 8px rgba(22,154,144,0.05), inset 0 -8px 0 rgba(0,0,0,0.05)",
      maxWidth: "380px", position: "relative",
    }}
  >
    <div style={{ position: "relative", zIndex: 1 }}>
      {step.title && <h3 style={{ color: "#1e293b", fontSize: "22px", fontWeight: "900", marginBottom: "10px", letterSpacing: "-0.5px" }}>{step.title}</h3>}
      <div style={{ color: "#475569", fontWeight: "500", fontSize: "16px", marginBottom: "30px", lineHeight: "1.5" }}>{step.content}</div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <button {...skipProps} style={{ color: "#94a3b8", fontWeight: "700", fontSize: "13px", textTransform: "uppercase", background: "none", border: "none", cursor: "pointer" }}>Skip</button>
        <div style={{ display: "flex", gap: "14px" }}>
          {index > 0 && (
            <button {...backProps} style={{ backgroundColor: "#f1f5f9", border: "none", borderRadius: "20px", padding: "10px 20px", fontWeight: "800", color: "#64748b", cursor: "pointer", boxShadow: "0 4px 0 #cbd5e1" }}>Back</button>
          )}
          <button {...primaryProps} style={{ background: "linear-gradient(180deg,#169a90 0%,#0d7a72 100%)", border: "none", borderRadius: "20px", padding: "10px 24px", fontWeight: "800", color: "white", cursor: "pointer", boxShadow: "0 6px 0 #085a54,0 12px 20px rgba(22,154,144,0.3)" }}>
            {isLastStep ? "Let's Explore!" : "Next"}
          </button>
        </div>
      </div>
    </div>
  </div>
);

// ─── TABS ─────────────────────────────────────────────────────────────────────

const TABS = [
  { id: "for-you",   label: "For You",     icon: Sparkles },
  { id: "my-paths",  label: "My Paths",    icon: Compass  },
  { id: "browse",    label: "Browse All",  icon: BookOpen },
];

// ─── Pagination ───────────────────────────────────────────────────────────────

const ITEMS_PER_PAGE = 12;

// ─── Stream → broad category mapping ─────────────────────────────────────────

const STREAM_MAP = {
  science:  ["STEM", "Health", "Agriculture"],
  arts:     ["Creative", "Humanities", "Education"],
  commerce: ["Business", "Law"],
};

// ─── Main component ───────────────────────────────────────────────────────────

export default function CareerExplorer() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("for-you");
  const [searchQuery, setSearchQuery] = useState("");
  const [runTour, setRunTour] = useState(false);

  // Filter + pagination state
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedStream, setSelectedStream] = useState("all");
  const [sortOrder, setSortOrder] = useState("best-match");
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);
  const sentinelRef = useRef(null);

  // Toast state
  const [toast, setToast] = useState(null); // { message, nodeId }
  const toastTimerRef = useRef(null);

  useEffect(() => {
    if (!localStorage.getItem("hasTakenTour_/career-explorer")) setRunTour(true);
  }, []);

  // ── Load student data — read from StudentStore (always current) ───────────
  const studentFromStore = StudentStore((s) => s.student);
  const traitProfile = studentFromStore?.traitProfile ?? null;
  const selectedBroadIds = studentFromStore?.selectedBroadPaths ?? [];

  // ── Build flat node lookup ─────────────────────────────────────────────────
  const allNodes = useMemo(() => getAllNodes(), []);
  const nodeById = useMemo(() => Object.fromEntries(allNodes.map((n) => [n.nodeId, n])), [allNodes]);

  // ── Field nodes (For You + My Paths) ──────────────────────────────────────
  const allFieldNodes = useMemo(() => allNodes.filter((n) => n.level === "field"), [allNodes]);
  const fieldNodeById = useMemo(() => Object.fromEntries(allFieldNodes.map(n => [n.nodeId, n])), [allFieldNodes]);

  const scoredNodes = useMemo(() => {
    return allFieldNodes.map((node) => ({
      node,
      matchPct: scoreNode(node, traitProfile),
    })).sort((a, b) => (b.matchPct ?? 0) - (a.matchPct ?? 0));
  }, [allFieldNodes, traitProfile]);

  // ── Specialization nodes (Browse All — all 500+ specific careers) ──────────
  const allSpecNodes = useMemo(() => allNodes.filter((n) => n.level === "specialization"), [allNodes]);

  const scoredSpecNodes = useMemo(() => {
    return allSpecNodes.map((node) => {
      // Inherit parent field's traits for scoring when spec traits are empty
      const scoringNode = node.traits?.length ? node : (fieldNodeById[node.parentId] ?? node);
      return { node, matchPct: scoreNode(scoringNode, traitProfile) };
    }).sort((a, b) => (b.matchPct ?? 0) - (a.matchPct ?? 0));
  }, [allSpecNodes, fieldNodeById, traitProfile]);

  // ── "For You" — top matched specific careers, capped ────────────────────
  const FOR_YOU_MAX = 12;
  const forYouNodes = useMemo(() => scoredSpecNodes.slice(0, FOR_YOU_MAX), [scoredSpecNodes]);

  // ── "My Paths" — field nodes connected to anything in selectedBroadPaths ───
  // selectedBroadPaths now stores actual picked node IDs (broad, field, or spec).
  const myPathNodes = useMemo(() => {
    if (!selectedBroadIds.length) return [];
    return scoredNodes.filter(({ node }) =>
      selectedBroadIds.some((id) => {
        if (id === node.parentId) return true;  // broad ID → all fields under it
        if (id === node.nodeId) return true;    // field ID → this exact field
        // spec ID → its parent field
        const info = nodeById[id];
        return info?.level === "specialization" && info.parentId === node.nodeId;
      })
    );
  }, [scoredNodes, selectedBroadIds, nodeById]);

  // ── "My Paths" — card data matching CareerPath.jsx shape ─────────────────
  const myPathCards = useMemo(() => {
    return selectedBroadIds.map((nodeId) => {
      if (BROAD_PATH_META[nodeId]) {
        const meta = BROAD_PATH_META[nodeId];
        return { id: nodeId, ...meta, broadCategory: nodeById[nodeId]?.broadCategory ?? "", progress: 0, focusLabel: null };
      }
      const info = nodeById[nodeId];
      if (!info) return null;
      let broadId = info.level === "field" ? info.parentId : nodeById[info.parentId]?.parentId ?? null;
      const broadMeta = BROAD_PATH_META[broadId] ?? { category: "Career", categoryColor: "text-slate-500" };
      return {
        id: nodeId,
        category: broadMeta.category,
        categoryColor: broadMeta.categoryColor,
        title: info.name,
        description: info.description || `${info.level === "field" ? "Field" : "Role"} within ${BROAD_PATH_META[broadId]?.title ?? "your career domain"}`,
        broadCategory: info.broadCategory ?? "",
        progress: 0,
        focusLabel: info.level === "specialization" ? (nodeById[info.parentId]?.name ?? null) : null,
      };
    }).filter(Boolean);
  }, [selectedBroadIds, nodeById]);

  // ── "Browse All" — all 500+ specific careers, searchable ─────────────────
  const browseNodes = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return scoredSpecNodes;
    return scoredSpecNodes.filter(({ node }) =>
      node.name.toLowerCase().includes(q) ||
      node.description?.toLowerCase().includes(q) ||
      node.broadCategory?.toLowerCase().includes(q) ||
      nodeById[node.parentId]?.name?.toLowerCase().includes(q)
    );
  }, [scoredSpecNodes, searchQuery, nodeById]);

  // ── "For You" also filterable by search ───────────────────────────────────
  const forYouFiltered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return forYouNodes;
    return forYouNodes.filter(({ node }) =>
      node.name.toLowerCase().includes(q) ||
      node.description.toLowerCase().includes(q) ||
      node.broadCategory?.toLowerCase().includes(q)
    );
  }, [forYouNodes, searchQuery]);

  // ── Unique categories for filter chips ────────────────────────────────────
  const categories = useMemo(() =>
    [...new Set(allFieldNodes.map((n) => n.broadCategory).filter(Boolean))].sort()
  , [allFieldNodes]);

  // ── Apply filters + sort across all tabs ──────────────────────────────────
  const filteredNodes = useMemo(() => {
    const base = activeTab === "for-you" ? forYouFiltered
      : activeTab === "my-paths" ? myPathNodes
      : browseNodes;

    let result = base;

    if (selectedCategories.length > 0) {
      result = result.filter(({ node }) => selectedCategories.includes(node.broadCategory));
    }
    if (selectedStream !== "all") {
      const cats = STREAM_MAP[selectedStream] ?? [];
      result = result.filter(({ node }) => cats.includes(node.broadCategory));
    }
    if (sortOrder === "az") {
      result = [...result].sort((a, b) => a.node.name.localeCompare(b.node.name));
    } else if (sortOrder === "newest") {
      result = [...result].sort((a, b) => b.node.name.localeCompare(a.node.name));
    }

    return result;
  }, [activeTab, forYouFiltered, myPathNodes, browseNodes, selectedCategories, selectedStream, sortOrder]);

  const displayedNodes = filteredNodes.slice(0, visibleCount);
  const hasMore = visibleCount < filteredNodes.length;
  const hasFilters = selectedCategories.length > 0 || selectedStream !== "all" || sortOrder !== "best-match";

  // ── Reset visible count when filters or tab change ─────────────────────────
  useEffect(() => {
    setVisibleCount(ITEMS_PER_PAGE);
  }, [activeTab, selectedCategories, selectedStream, sortOrder, searchQuery]);

  // ── Infinite scroll via IntersectionObserver ───────────────────────────────
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVisibleCount((prev) => prev + ITEMS_PER_PAGE);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, filteredNodes]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleViewPath = (nodeId) => navigate(`/career-learning-path?id=${nodeId}`);

  const showToast = useCallback((message, type) => {
    clearTimeout(toastTimerRef.current);
    setToast({ message, type });
    toastTimerRef.current = setTimeout(() => setToast(null), 3000);
  }, []);

  const handleAddToPath = useCallback((nodeId) => {
    const existing = StudentStore.getState().student?.selectedBroadPaths ?? [];
    if (existing.includes(nodeId)) {
      showToast("Already in My Paths", "already");
      return;
    }

    const updated = [...existing, nodeId];

    // Update Zustand store (triggers reactive re-render)
    StudentStore.getState().updateStudent({ selectedBroadPaths: updated });

    // Keep StorageService in sync
    const currentUser = AuthStore.getState().currentUser;
    if (currentUser) {
      const students = StorageService.read("students") ?? [];
      const idx = students.findIndex((s) => s.userId === currentUser._id || s.email === currentUser.email);
      if (idx !== -1) {
        students[idx] = { ...students[idx], selectedBroadPaths: updated, updatedAt: new Date() };
        StorageService.write("students", students);
      }
    }

    showToast("Added to My Paths", "added");
  }, [showToast]);

  const toggleCategory = (cat) => {
    setSelectedCategories((prev) =>
      prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]
    );
  };

  const clearFilters = () => {
    setSelectedCategories([]);
    setSelectedStream("all");
    setSortOrder("best-match");
  };

  const handleJoyrideCallback = ({ status }) => {
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      localStorage.setItem("hasTakenTour_/career-explorer", "true");
      setRunTour(false);
    }
  };

  const tourSteps = [
    { target: ".hidden.md\\:block", title: "Discover Your Future", content: "Browse and discover career paths matched to your unique trait profile.", placement: "bottom" },
    { target: ".explorer-search", title: "Search Anything", content: "Filter by career name, category, or key skills.", placement: "bottom" },
    { target: ".explorer-grid", title: "Career Library", content: "Each card shows your match %, the broad category, and the traits that matter most for that career.", placement: "top" },
  ];

  return (
    <ProtectedRoute>
      <style>{`
        @keyframes boxPopIn { 0%{transform:scale(0.9) translateY(20px);opacity:0} 100%{transform:scale(1) translateY(0);opacity:1} }
        .tour-3d-box { animation: boxPopIn 0.5s cubic-bezier(0.34,1.56,0.64,1); }
      `}</style>

      <div className="flex min-h-screen bg-slate-100">
        <Sidebar />
        
        <UniversalSearch onSearch={() => {}} />

        <main className="flex-1 lg:ml-64 p-3 sm:p-4 md:p-6 lg:p-8">
          {/* Mobile heading */}
          <div className="md:hidden flex items-center gap-3 mb-4 pb-4 border-b border-slate-200">
            <div className="flex-1">
              <h1 className="text-lg font-bold text-slate-900">Career Explorer</h1>
              <p className="text-sm text-slate-600">Discover your future career</p>
            </div>
          </div>

          <div className="hidden md:block">
            <ExplorerHeader />
          </div>

          {/* Search bar */}
          <div className="mt-4 sm:mt-6 explorer-search">
            <div className="relative max-w-xl">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search careers, categories, skills…"
                className="w-full bg-white border border-slate-200 rounded-xl pl-10 pr-10 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
              />
              {searchQuery && (
                <button onClick={() => setSearchQuery("")} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Filter bar */}
          <div className="mt-4 space-y-2.5">
            {/* Category chips */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wide shrink-0 w-16">Category</span>
              {categories.map((cat) => {
                const style = getCategoryStyle(cat);
                const isActive = selectedCategories.includes(cat);
                return (
                  <button
                    key={cat}
                    onClick={() => toggleCategory(cat)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                      isActive
                        ? `${style.bg} ${style.border} ${style.text} shadow-sm`
                        : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
                    }`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${isActive ? style.dot : "bg-slate-300"}`} />
                    {cat}
                  </button>
                );
              })}
            </div>

            {/* Stream + Sort + Clear */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wide shrink-0 w-16">Stream</span>
              {[
                { id: "all",      label: "All" },
                { id: "science",  label: "Science" },
                { id: "arts",     label: "Arts" },
                { id: "commerce", label: "Commerce" },
              ].map(({ id, label }) => (
                <button
                  key={id}
                  onClick={() => setSelectedStream(id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                    selectedStream === id
                      ? "bg-blue-600 border-blue-600 text-white shadow-sm"
                      : "bg-white border-slate-200 text-slate-600 hover:border-blue-300 hover:text-blue-700"
                  }`}
                >
                  {label}
                </button>
              ))}

              <div className="ml-auto flex items-center gap-2">
                <select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  className="bg-white border border-slate-200 rounded-lg px-3 py-1.5 text-xs font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400 cursor-pointer"
                >
                  <option value="best-match">Best Match</option>
                  <option value="az">A – Z</option>
                  <option value="newest">Z – A</option>
                </select>

                {hasFilters && (
                  <button
                    onClick={clearFilters}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 border border-red-200 text-red-600 hover:bg-red-100 rounded-lg text-xs font-semibold transition-all"
                  >
                    <X className="w-3 h-3" />
                    Clear
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="mt-5 flex items-center gap-2 flex-wrap">
            {TABS.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                  activeTab === id
                    ? "bg-blue-600 text-white shadow-sm"
                    : "bg-white border border-slate-200 text-slate-600 hover:border-blue-300 hover:text-blue-700"
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
                {id === "my-paths" && myPathCards.length > 0 && (
                  <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ml-0.5 ${activeTab === id ? "bg-white/20" : "bg-blue-100 text-blue-600"}`}>
                    {myPathCards.length}
                  </span>
                )}
              </button>
            ))}

            {traitProfile && (
              <span className="ml-auto text-xs text-slate-400 flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                Trait profile active · {Object.keys(traitProfile).length} traits scored
              </span>
            )}
          </div>

          {/* Tab heading */}
          <div className="mt-6 mb-4 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold text-slate-900">
                {activeTab === "for-you" && "Careers Matched to Your Profile"}
                {activeTab === "my-paths" && "Careers in Your Active Paths"}
                {activeTab === "browse" && "All Careers"}
              </h3>
              <p className="text-sm text-slate-500 mt-0.5">
                {activeTab === "for-you" && (traitProfile ? "Ranked by how closely your 49 traits match each field's requirements" : "Complete the assessment to see personalised rankings")}
                {activeTab === "my-paths" && (selectedBroadIds.length ? `Fields within: ${selectedBroadIds.map(id => id.replace("broad_","").replace(/_/g," ")).join(", ")}` : "You haven't chosen any career paths yet")}
                {activeTab === "browse" && `${filteredNodes.length} careers${hasFilters ? " (filtered)" : ` across ${allFieldNodes.length} fields`}`}
              </p>
            </div>
          </div>

          {/* Content */}
          {activeTab === "for-you" && !traitProfile ? (
            <NoProfileCTA navigate={navigate} />
          ) : activeTab === "my-paths" && !myPathCards.length ? (
            <div className="flex flex-col items-center justify-center py-20 text-center max-w-sm mx-auto">
              <div className="w-16 h-16 rounded-2xl bg-blue-50 border border-blue-200 flex items-center justify-center mb-5">
                <Compass className="w-8 h-8 text-blue-500" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">No active paths yet</h3>
              <p className="text-slate-500 text-sm mb-6">Add a career path to see it here.</p>
              <button
                onClick={() => navigate("/broad-path-selection?mode=add")}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold px-6 py-3 rounded-xl transition-colors"
              >
                Choose a Career Path <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          ) : activeTab === "my-paths" ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {myPathCards.map((card) => (
                <CareerPathCard key={card.id} {...card} />
              ))}
            </div>
          ) : filteredNodes.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              {hasFilters || searchQuery
                ? "No careers match the current filters."
                : `No careers found matching "${searchQuery}".`}
            </div>
          ) : (
            <>
              <div className="explorer-grid grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {displayedNodes.map(({ node, matchPct }) => (
                  <CareerCard
                    key={node.nodeId}
                    node={node}
                    matchPct={matchPct}
                    traitProfile={traitProfile}
                    onViewPath={handleViewPath}
                    onAddToPath={handleAddToPath}
                    isAdded={selectedBroadIds.includes(node.nodeId) || selectedBroadIds.includes(node.parentId)}
                  />
                ))}
              </div>

              {/* Infinite scroll sentinel */}
              {hasMore ? (
                <div ref={sentinelRef} className="flex items-center justify-center gap-2 py-8 text-sm text-slate-400">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading more…
                </div>
              ) : (
                <div className="flex items-center justify-center gap-3 py-8">
                  <div className="h-px flex-1 bg-slate-200" />
                  <span className="text-xs text-slate-400 font-medium">All caught up · {filteredNodes.length} careers</span>
                  <div className="h-px flex-1 bg-slate-200" />
                </div>
              )}
            </>
          )}
        </main>

        <Joyride
          steps={tourSteps}
          run={runTour}
          continuous
          showProgress
          showSkipButton
          callback={handleJoyrideCallback}
          tooltipComponent={Animated3DTooltip}
          floaterProps={{ disableAnimation: true }}
          styles={{ options: { zIndex: 10000, overlayColor: "rgba(15,23,42,0.65)" }, spotlight: { borderRadius: "24px" } }}
        />

        {/* Toast notification */}
        {toast && (
          <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2.5 px-5 py-3 rounded-2xl shadow-lg text-sm font-semibold transition-all animate-[fadeSlideUp_0.25s_ease] ${
            toast.type === "added"
              ? "bg-emerald-600 text-white"
              : "bg-slate-700 text-white"
          }`}>
            <CheckCircle2 className="w-4 h-4 shrink-0" />
            {toast.message}
          </div>
        )}
        <style>{`@keyframes fadeSlideUp { from { opacity: 0; transform: translate(-50%, 12px); } to { opacity: 1; transform: translate(-50%, 0); } }`}</style>
      </div>
    </ProtectedRoute>
  );
}
